import { useState } from "react";
import { createColumnHelper } from "@tanstack/react-table";
import { Spinner, Stack } from "react-bootstrap";
import { format } from 'date-fns';
import { ServiceManager } from "../../../services/ServiceManager";
import PagedFilterTable from "../../../components/table";
import IExtractFile from "../../../models/ExtractFile";
import { useParams } from "react-router-dom";
import { toGuid } from "../../../app/common/helper/Guid";
import CustomModal from "../../../components/modal";
import { useQuery } from "@tanstack/react-query";
import { MisType } from "../../../app/enums";
import XMLViewer from 'react-xml-viewer';
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSpinner } from "@fortawesome/free-solid-svg-icons";
import ReactJson from 'react-json-view'

export default function RawXml() {

    const { establishmentId } = useParams();
    const [tableJobs, setTableJobs] = useState([]);
    const [showFileContentModal, setShowFileContentModal] = useState(false);
    const [fileId, setFileId] = useState();
    const [fileName, setFileName] = useState("");
    const [misType, setMisType] = useState(MisType.Unknown);

    const { isLoading: isUserTypesLoading, data: userTypes } = useQuery({
        queryKey: [`establishment-user-types-${establishmentId}`],
        queryFn: () => ServiceManager.UserTypeService.GetByEstablishmentId(establishmentId),
        refetchOnWindowFocus: false,
        enabled: !!establishmentId,
    });

    const customXmlTheme = {
        "attributeKeyColor": "#FF0000",
        "attributeValueColor": "#000FF"
    };

    const handleCloseXMLModal = () => { setShowFileContentModal(false); setFileId(undefined); }

    const columnHelper = createColumnHelper<IExtractFile>();
    const columns = [
        columnHelper.accessor('fileName', {
            header: () => <span>File Name</span>
        }),
        columnHelper.accessor('misType', {
            header: () => <span>MIS</span>,
            cell: ({ row }) => (
                <div className="short-text-columns">
                    <span>{row.original.dateCreated ? MisType[row?.original?.misType]?.toUpperCase() : ''}</span>
                </div>

            ),
        }),
        columnHelper.accessor('userTypeId', {
            header: () => <span>User Type</span>,
            cell: ({ row }) => (
                <div className="short-text-columns">
                    <span>{(!isUserTypesLoading && userTypes && row.original.userTypeId) ? userTypes.find((ut: any) => ut.userTypeID === row.original.userTypeId)?.userTypeName : ''}</span>
                </div>
            ),
        }),
        columnHelper.accessor('isPhotos', {
            header: () => <span>Report Type</span>,
            cell: ({ row }) => (
                <div className="short-text-columns">
                    <span>{(!isUserTypesLoading && userTypes && row.original.userTypeId) ? userTypes.find((ut: any) => ut.userTypeID === row.original.userTypeId)?.userTypeName + ' ' + (row.original.isPhotos ? "Photos" : "") : ''}</span>
                </div>
            ),
        }),
        columnHelper.accessor('dateCreated', {
            header: () => <span>Date</span>,
            cell: ({ row }) => (
                <Stack>
                    <span>{row.original.dateCreated ? format(new Date(row.original.dateCreated), "dd/MM/yyyy HH:mm") : ''}</span>
                </Stack>
            )
        }),
        columnHelper.accessor("fileId", {
            header: () => <span></span>,
            cell: ({ row }) => (
                <div className="text-center">
                    {(fileId !== row.original.fileId) && <button className="btn btn-outline-secondary" onClick={() => processViewXML(row.original.fileId, row.original.fileName, row.original.misType)}>View</button>}
                    {(fileId === row.original.fileId) && <button className="btn btn-outline-secondary"><FontAwesomeIcon icon={faSpinner} className="fa-spin" /></button>}
                </div>
            ),
            size: 10,
        })
    ];

    const processViewXML = (fileId: any, fileName: any, misType: MisType) => {
        setFileId(fileId);
        setFileName(fileName);
        setMisType(misType);
        setShowFileContentModal(true);
    }

    const FileContent = () => {
        const { isLoading, data } = useQuery({
            queryKey: [`file-content-${fileId}`],
            queryFn: () => ServiceManager.EstablishmentService.GetExtractFile(establishmentId, fileId ?? 0),
            refetchOnWindowFocus: false,
        });

        return (
            <Stack>
                {!isLoading
                    ? (
                        <Stack direction="horizontal" className="mt-3">
                            {misType === MisType.Sims && <XMLViewer xml={data} theme={customXmlTheme} collapsible={false} wrap={false} />}
                            {misType === MisType.Bromcom && <ReactJson src={data} name={null} enableClipboard={false} displayDataTypes={false} collapsed={1} />}
                            {misType === MisType.Arbor && <ReactJson src={data} name={null} enableClipboard={false} displayDataTypes={false} collapsed={1} />}
                            </Stack>
                        ) : (
                            <div className="d-flex justify-content-center mt-3">
                                <Spinner
                                    animation="border"
                                    variant="info"
                                    role="status"
                                    style={{ width: "4rem", height: "4rem" }}
                                >
                                </Spinner>
                            </div>
                        )
                }
            </Stack>
        );
    }

    async function fetchData(options: {
        pageIndex: number;
        pageSize: number;
        searchValue: string
    }) {
        const transformData = await ServiceManager.EstablishmentService.GetExtractFiles(toGuid(establishmentId ?? ""), options.pageIndex + 1,
            options.pageSize);
        setTableJobs(transformData);
        return transformData;
    };

    return (
        <>
            <div id="listView-data-rawxml" className="mt-2">
                <PagedFilterTable
                    tableKey={`extract-files-${establishmentId}`}
                    columns={columns}
                    data={tableJobs}
                    usePagination={true}
                    pageRecords={10}
                    fnFetchData={fetchData}
                    useSearch={false}
                />
            </div>

            <CustomModal isShow={showFileContentModal} handleClose={handleCloseXMLModal} header={<span className="xml-header-file mb-3">{fileName}</span>} size="xl" fullscreen={true} headerBgColor={'#8394aa'}>
                {showFileContentModal && <FileContent />}
            </CustomModal>
        </>

    )
}